import { apiFetch, getToken } from './apiFetch';
import type {
  ClosedRunSummary,
  PayrollReportResponse,
  PeriodSelection,
  RecomputeDiff,
} from '@salaries/shared';

// Body shared by all run actions: the period selection (+ optional staff_ids
// / pnl_unit_id).
function selBody(sel: PeriodSelection, extra: Record<string, unknown> = {}) {
  const b: Record<string, unknown> = { kind: sel.kind, ...extra };
  if (sel.kind === 'custom') {
    b.start_date = sel.start_date;
    b.end_date = sel.end_date;
  } else if (sel.kind === 'month') {
    b.year = sel.year;
    b.month = sel.month;
  }
  return JSON.stringify(b);
}

// Closing, recomputing and the accountant report are all full rebuilds that run
// for minutes. They are QUEUED server-side and return immediately; the caller
// tells the operator it's running and then watches with awaitRunJob below.
export type RunJobKind =
  | 'lock_run'
  | 'recompute_preview'
  | 'recompute_apply'
  | 'accountant_report';

export interface RunJobStatus<T = unknown> {
  state: 'none' | 'pending' | 'done' | 'failed';
  result: T | null;
  error: string | null;
}

// `job` is the job kind; `kind` stays the PERIOD selector the server already
// parses everywhere else. Don't collapse these two into one param.
function statusQuery(sel: PeriodSelection, kind: RunJobKind, unitId?: number): string {
  const p = new URLSearchParams({ job: kind });
  p.set('kind', sel.kind);
  if (sel.kind === 'custom') {
    p.set('start_date', sel.start_date);
    p.set('end_date', sel.end_date);
  } else if (sel.kind === 'month') {
    p.set('year', String(sel.year));
    p.set('month', String(sel.month));
  }
  if (unitId != null) p.set('pnl_unit_id', String(unitId));
  return p.toString();
}

export async function getRunJobStatus<T>(
  sel: PeriodSelection,
  kind: RunJobKind,
  unitId?: number,
): Promise<RunJobStatus<T>> {
  return apiFetch(`/payroll/run/job-status?${statusQuery(sel, kind, unitId)}`);
}

/**
 * Poll a queued run job to completion WITHOUT blocking the UI — no loader, no
 * disabled screen. The operator keeps working; we just report the outcome when
 * it lands. Resolves with the job's result, or rejects with its error.
 */
export function awaitRunJob<T>(
  sel: PeriodSelection,
  kind: RunJobKind,
  opts: { intervalMs?: number; timeoutMs?: number; unitId?: number } = {},
): Promise<T | null> {
  const intervalMs = opts.intervalMs ?? 4000;
  const timeoutMs = opts.timeoutMs ?? 20 * 60_000;
  const startedAt = Date.now();
  return new Promise<T | null>((resolve, reject) => {
    const tick = async () => {
      if (Date.now() - startedAt > timeoutMs) {
        reject(new Error('החישוב נמשך זמן רב מהצפוי. רעננו את הדף לבדיקה.'));
        return;
      }
      try {
        const s = await getRunJobStatus<T>(sel, kind, opts.unitId);
        if (s.state === 'done') return resolve(s.result);
        if (s.state === 'failed') return reject(new Error(s.error || 'הפעולה נכשלה'));
      } catch {
        // Transient (a deploy, a blip) — keep watching rather than giving up.
      }
      window.setTimeout(() => void tick(), intervalMs);
    };
    window.setTimeout(() => void tick(), 1500);
  });
}

/** Close the period. `unitId` scopes it to one P&L unit; omitted = every unit.
 *  Queued — 202, then watch with awaitRunJob('lock_run', {unitId}). */
export async function lockRun(sel: PeriodSelection, unitId?: number): Promise<{ queued: boolean }> {
  return apiFetch('/payroll/run/lock', { method: 'POST', body: selBody(sel, { pnl_unit_id: unitId }) });
}

/** Reopen a locked period — the report goes live again. `unitId` scopes it to
 *  one P&L unit; omitted = every unit. */
export async function reopenRun(sel: PeriodSelection, unitId?: number): Promise<{ status: string }> {
  return apiFetch('/payroll/run/reopen', { method: 'POST', body: selBody(sel, { pnl_unit_id: unitId }) });
}

/** Queue the diff of live Arbox vs the frozen snapshots (omit staffIds = all).
 *  The diff itself arrives via awaitRunJob<RecomputeDiff>('recompute_preview').
 *  `unitId` restricts it to one (already-locked) P&L unit; omitted = every
 *  currently-locked unit. */
export async function recomputePreview(
  sel: PeriodSelection,
  staffIds?: number[],
  unitId?: number,
): Promise<{ queued: boolean }> {
  return apiFetch('/payroll/run/recompute-preview', {
    method: 'POST',
    body: selBody(sel, { ...(staffIds?.length ? { staff_ids: staffIds } : {}), pnl_unit_id: unitId }),
  });
}

/** Queue the re-freeze of the chosen staff to the latest Arbox data. */
export async function recomputeApply(
  sel: PeriodSelection,
  staffIds: number[],
  unitId?: number,
): Promise<{ queued: boolean }> {
  return apiFetch('/payroll/run/recompute-apply', {
    method: 'POST',
    body: selBody(sel, { staff_ids: staffIds, pnl_unit_id: unitId }),
  });
}

/** What a finished recompute_apply job returns. */
export interface RecomputeApplyResult {
  applied: number;
  voided: number;
}

/** Mark sent/approved lines as final. */
export async function finalizeLines(
  sel: PeriodSelection,
  staffIds: number[],
  /** Restrict to one P&L-unit line — "סמן כסופי" is per row, and a coach's other
   *  branches must not be frozen along with the one being confirmed. */
  unitId?: number,
): Promise<{ finalized: number }> {
  return apiFetch('/payroll/run/finalize', {
    method: 'POST',
    body: selBody(sel, { staff_ids: staffIds, pnl_unit_id: unitId }),
  });
}

/** The closed-runs archive list. */
export async function listClosedRuns(): Promise<ClosedRunSummary[]> {
  return apiFetch('/payroll/runs');
}

/** A closed run's frozen report for one P&L unit (0 for a box that doesn't
 *  split — the archive lists one row per (period, unit), so a caller always
 *  has one to pass). */
export async function getClosedRunReport(
  start: string,
  end: string,
  unitId: number,
): Promise<PayrollReportResponse> {
  return apiFetch(`/payroll/runs/report?start_date=${start}&end_date=${end}&pnl_unit_id=${unitId}`);
}

/** Queue the accountant email (Excel + PDF + invoice ZIP) for one P&L unit's
 *  closed run (or every currently-locked unit combined when `unitId` is
 *  omitted). Returns as soon as it's queued — the recipient comes back
 *  immediately, the send happens after. */
export async function sendRunReport(
  start: string,
  end: string,
  unitId?: number,
): Promise<{ queued: boolean; to: string }> {
  return apiFetch('/payroll/runs/send-report', {
    method: 'POST',
    body: JSON.stringify({ start_date: start, end_date: end, pnl_unit_id: unitId }),
  });
}

/** Download a ZIP of all invoices/receipts uploaded for a closed run, scoped to
 *  one P&L unit when given (auth'd blob → anchor). */
export async function downloadClosedRunInvoices(
  start: string,
  end: string,
  unitId?: number,
): Promise<void> {
  const q = new URLSearchParams({ start_date: start, end_date: end });
  if (unitId != null) q.set('pnl_unit_id', String(unitId));
  const res = await fetch(`/api/payroll/runs/invoices?${q.toString()}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (res.status === 404) throw new Error('אין חשבוניות להורדה בתקופה זו');
  if (!res.ok) throw new Error('שגיאה בהורדת החשבוניות');
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^";]+)"?/);
  const filename = m ? m[1]! : `invoices_${start}_${end}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
